import type { NetworkAvailableFeature } from '../../auth/networkFeatures';
import { ShigoDashboardContext } from '../../auth/dashboardDestination';
import { dashboardT } from '../../../lib/messages';
import { isNumericReportMetric, reportDimensionDefinitions } from './catalog';
import { availableDimensionSections, availableMetricSections } from './reportCatalogAvailability';
import { reportMetricLabel } from './format';
import type { ReportSelectionSection } from './components/ReportSelectionDialog';
import type { ReportExplorerConfiguration } from './model';
import type { ReportDimension, ReportMetric } from './reportEnums';
import { publisherReportFilterFields } from './reportFilters';

export function metricSelectionSections(
  features: readonly NetworkAvailableFeature[],
  numericOnly: boolean,
  dashboardContext = ShigoDashboardContext.Main,
): ReportSelectionSection<ReportMetric>[] {
  return availableMetricSections(features, dashboardContext).flatMap((section) => {
    const items = numericOnly
      ? section.items.filter(({ metric }) => isNumericReportMetric(metric))
      : section.items;
    return items.length
      ? [
          {
            id: section.group,
            labelKey: section.labelKey,
            options: items.map(({ metric, descriptionKey }) => ({
              id: metric,
              label: reportMetricLabel(metric),
              ...(descriptionKey
                ? {
                    description: dashboardT(descriptionKey),
                  }
                : {}),
            })),
          },
        ]
      : [];
  });
}

export function dimensionSelectionSections(
  features: readonly NetworkAvailableFeature[],
  dashboardContext = ShigoDashboardContext.Main,
): ReportSelectionSection<ReportDimension>[] {
  return availableDimensionSections(features, dashboardContext).map((section) => ({
    id: section.group,
    labelKey: section.labelKey,
    options: section.items.map(({ dimension }) => ({
      id: dimension,
      label: dashboardT(reportDimensionDefinitions[dimension].labelKey),
    })),
  }));
}

export function sanitizeReportExplorerConfiguration(
  configuration: ReportExplorerConfiguration,
  features: readonly NetworkAvailableFeature[],
  dashboardContext: ShigoDashboardContext,
): ReportExplorerConfiguration {
  const dimensions = new Set(
    availableDimensionSections(features, dashboardContext).flatMap((section) =>
      section.items.map(({ dimension }) => dimension),
    ),
  );
  const metrics = new Set(
    availableMetricSections(features, dashboardContext).flatMap((section) =>
      section.items.map(({ metric }) => metric),
    ),
  );
  const chartMetrics = new Set(
    metricSelectionSections(features, true, dashboardContext).flatMap((section) =>
      section.options.map(({ id }) => id),
    ),
  );
  return {
    ...configuration,
    tableDimensions: configuration.tableDimensions.filter((dimension) => dimensions.has(dimension)),
    tableMetrics: configuration.tableMetrics.filter((metric) => metrics.has(metric)),
    chartMetrics: configuration.chartMetrics.filter((metric) => chartMetrics.has(metric)),
    reportFilters: configuration.reportFilters.filter(
      ({ field }) =>
        (dashboardContext !== ShigoDashboardContext.Publisher &&
          dashboardContext !== ShigoDashboardContext.Agency) ||
        publisherReportFilterFields.has(field),
    ),
  };
}
